// @flow

import * as React from "react";
import chunk from "lodash/chunk";
import cx from "classnames";
import Popover from "../../Popover";
import TextInput from "../../Input/TextInput";
import DatePickersLogic from "../shared/DatePickersLogic";
import PickerNav from "../shared/PickerNav";
import { isMobile, isIPhone, isIPad } from "../../../utils/deviceDetection";
import NativeDatePicker from "../NativeDatePicker/NativeDatePicker";

import type { DaysPropsType } from "../shared/DatePickersLogic";
import type { InputType } from "../../Input/TextInput";

import styles from "./DatePicker.scss";
import {
  datePickerDisplayFormat as datePickerDisplayFormatDefault,
  datePickerFormFormat as datePickerFormFormatDefault
} from "../../../utils/formats";

type DatePickerProps = {
  input: InputType,
  startDate: Date,
  minDate: Date,
  maxDate: Date,
  shouldDisallowDate: Date => boolean,
  placeholder: string,
  datePickerDisplayFormat: string,
  datePickerFormFormat: string,
  isDisabled?: boolean
};

const renderHeaders = (weekDays: Array<string>) => (
  <div className={styles.headerRow}>
    {weekDays.map(day => (
      <div className={styles.weekDayHeader} key={day}>
        {day.charAt(0)}
      </div>
    ))}
  </div>
);

const renderDays = (daysProps: DaysPropsType, closePopover: () => void) =>
  chunk(daysProps, 7).map((row: DaysPropsType, rowIndex: number) => (
    // eslint-disable-next-line react/no-array-index-key
    <div className={styles.dayRow} key={`row${rowIndex}`}>
      {row.map((paramsForDay, dayIndex) => {
        const key = `day${dayIndex}`;

        if (!paramsForDay) {
          return <div className={styles.emptyDay} key={key} />;
        }

        const { day, onClick, isSelectable, isSelected, isLastDay } = paramsForDay;

        if (!isSelectable) {
          return (
            <div key={key} className={cx(styles.day, styles.disabledDay)}>
              {day}
            </div>
          );
        }

        return (
          <button
            className={cx(styles.day, {
              [styles.selectedDay]: isSelected
            })}
            type="button"
            key={key}
            onClick={isSelectable ? () => onClick(closePopover) : undefined}
            data-closeontab={isLastDay}
          >
            {day}
          </button>
        );
      })}
    </div>
  ));

const renderDaysGrid = (
  daysProps: Array<null | {
    day: number,
    onClick: (() => void) => void,
    isSelectable: boolean,
    isSelected: boolean,
    isLastDay: boolean
  }>,
  weekDays: Array<string>,
  closePopover: () => void
) => (
  <div className={styles.daysWrapper}>
    {renderHeaders(weekDays)}
    {renderDays(daysProps, closePopover)}
  </div>
);

const DatePicker = ({
  input,
  startDate,
  minDate,
  maxDate,
  placeholder,
  datePickerDisplayFormat,
  datePickerFormFormat,
  shouldDisallowDate,
  isDisabled
}: DatePickerProps) =>
  isMobile() && !isIPhone() && !isIPad() ? (
    <NativeDatePicker
      input={input}
      minDate={minDate}
      maxDate={maxDate}
      placeholder={placeholder}
      isDisabled={isDisabled}
    />
  ) : (
    <DatePickersLogic
      input={input}
      startDate={startDate}
      minDate={minDate}
      maxDate={maxDate}
      pickerType="date"
      datePickerDisplayFormat={datePickerDisplayFormat}
      datePickerFormFormat={datePickerFormFormat}
      shouldDisallowDate={shouldDisallowDate}
    >
      {({
        datePickerProps: { daysProps, weekDays, datePickerValue, onOpenDatePicker },
        monthNavProps: {
          label,
          setPreviousMonth,
          setNextMonth,
          isPreviousMonthSelectable,
          isNextMonthSelectable,
          ariaLabelPrevious,
          ariaLabelNext
        },
        shared: { onKeyDown }
      }) => (
        <Popover
          input={({ onFocus }) => (
            <TextInput
              input={{
                ...input,
                onFocus: e => {
                  onFocus(e);
                  input.onFocus(e);
                },
                value: datePickerValue
              }}
              placeholder={placeholder}
              readOnly
              onKeyDown={onKeyDown}
              data-closeonshifttab
              data-testid={`dateInput:${input.name}`}
              sideSymbol={() => <i className="icon-calendar-empty" />}
              isDisabled={isDisabled}
            />
          )}
          isDisabled={isDisabled}
          onOpen={onOpenDatePicker}
          content={({ closePopover }) => (
            <div className={styles.popperWrapper}>
              <PickerNav
                label={label}
                setPrevious={setPreviousMonth}
                setNext={setNextMonth}
                isPreviousArrowSelectable={isPreviousMonthSelectable}
                isNextArrowSelectable={isNextMonthSelectable}
                ariaLabelPrevious={ariaLabelPrevious}
                ariaLabelNext={ariaLabelNext}
              />
              {renderDaysGrid(daysProps, weekDays, closePopover)}
            </div>
          )}
        />
      )}
    </DatePickersLogic>
  );

DatePicker.defaultProps = {
  startDate: new Date(),
  minDate: new Date(0, 0, 1),
  maxDate: new Date(2999, 11, 31),
  shouldDisallowDate: () => false,
  placeholder: "",
  datePickerDisplayFormat: datePickerDisplayFormatDefault,
  datePickerFormFormat: datePickerFormFormatDefault
};

export default DatePicker;
